import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

export interface BetSlipLeg {
  selection_id: string;
  market_id: string;
  event_id: string;
  event_name: string;
  market_name: string;
  selection_name: string;
  odds: number;
  stake_cents: number;
}

type BetType = 'single' | 'accumulator';

interface BetSlipContextValue {
  legs: BetSlipLeg[];
  betType: BetType;
  totalStake: number;
  potentialPayout: number;
  addLeg: (leg: BetSlipLeg) => void;
  removeLeg: (selection_id: string) => void;
  updateStake: (selection_id: string, cents: number) => void;
  clearSlip: () => void;
  setBetType: (type: BetType) => void;
}

const BetSlipContext = createContext<BetSlipContextValue | undefined>(undefined);

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const [legs, setLegs] = useState<BetSlipLeg[]>([]);
  const [betType, setBetType] = useState<BetType>('single');

  const addLeg = useCallback((leg: BetSlipLeg) => {
    setLegs((prev) => {
      // Replace if selection already exists
      const exists = prev.findIndex((l) => l.selection_id === leg.selection_id);
      if (exists >= 0) {
        const next = [...prev];
        next[exists] = leg;
        return next;
      }
      return [...prev, leg];
    });
  }, []);

  const removeLeg = useCallback((selection_id: string) => {
    setLegs((prev) => prev.filter((l) => l.selection_id !== selection_id));
  }, []);

  const updateStake = useCallback((selection_id: string, cents: number) => {
    setLegs((prev) =>
      prev.map((l) =>
        l.selection_id === selection_id ? { ...l, stake_cents: cents } : l,
      ),
    );
  }, []);

  const clearSlip = useCallback(() => {
    setLegs([]);
    setBetType('single');
  }, []);

  // For an accumulator, the stake is a single amount applied to the whole bet.
  // We store it on legs[0] and keep all other legs' stake_cents in sync via
  // updateStake so there's a single source of truth.
  const accumulatorStake = useMemo(
    () => (betType === 'accumulator' ? (legs[0]?.stake_cents ?? 0) : 0),
    [legs, betType],
  );

  const totalStake = useMemo(() => {
    if (betType === 'accumulator') return accumulatorStake;
    return legs.reduce((sum, l) => sum + l.stake_cents, 0);
  }, [legs, betType, accumulatorStake]);

  const potentialPayout = useMemo(() => {
    if (legs.length === 0) return 0;
    if (betType === 'accumulator') {
      const combinedOdds = legs.reduce((acc, l) => acc * l.odds, 1);
      return Math.round(accumulatorStake * combinedOdds);
    }
    // Singles: sum of each leg's individual payout
    return legs.reduce((sum, l) => sum + Math.round(l.stake_cents * l.odds), 0);
  }, [legs, betType, accumulatorStake]);

  return (
    <BetSlipContext.Provider
      value={{
        legs,
        betType,
        totalStake,
        potentialPayout,
        addLeg,
        removeLeg,
        updateStake,
        clearSlip,
        setBetType,
      }}
    >
      {children}
    </BetSlipContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBetSlip(): BetSlipContextValue {
  const ctx = useContext(BetSlipContext);
  if (!ctx) {
    throw new Error('useBetSlip must be used within a BetSlipProvider');
  }
  return ctx;
}
